import { ProjectRenderer } from '../ProjectRenderer';
import { OrbitControls } from './OrbitControls';
import { debounce } from 'lodash';
import { ThreeEvent } from '../types';
import { PerspectiveCamera, Vector3, Box3 } from 'three';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import gsap from 'gsap';

export class CameraController {
	camera!: PerspectiveCamera;
	screenshotCamera!: PerspectiveCamera;
	controls!: OrbitControls;

	constructor(private renderer: ProjectRenderer) {
		this.init();
	}

	private init() {
		this.createCameras();
		this.createControls();
	}

	private createCameras() {
		this.camera = new PerspectiveCamera();
		this.screenshotCamera = new PerspectiveCamera();
		const aspect =
			this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;

		for (const camera of [this.camera, this.screenshotCamera]) {
			camera.layers.enableAll();
			camera.aspect = aspect;
			camera.position.set(2, 2, 2);
			camera.updateProjectionMatrix();
		}
	}

	private createControls() {
		this.controls = new OrbitControls(this.camera, this.renderer.canvas);
		this.controls.target.set(0, 0, 0);

		this.controls.maxDistance = RENDERER_CONFIG.CAMERA.MAX_DISTANCE;
		this.controls.minDistance = RENDERER_CONFIG.CAMERA.MIN_DISTANCE;

		this.controls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;

		this.controls.addEventListener(
			'change',
			debounce(() => {
				const distance = this.camera.position.distanceTo(this.controls.target);

				this.renderer.emitter.emit(ThreeEvent.DISTANCE, distance);
			}, 150)
		);

		this.controls.saveState();
	}

	prepareScreenshotCamera() {
		const box = new Box3().setFromObject(this.renderer.project);
		const size = new Vector3();
		box.getSize(size);
		const length = size.length();

		const aspect =
			RENDERER_CONFIG.SCREENSHOT.WIDTH / RENDERER_CONFIG.SCREENSHOT.HEIGHT;
		this.screenshotCamera.aspect = aspect;
		this.screenshotCamera.updateProjectionMatrix();

		const center = box.getCenter(new Vector3());

		this.screenshotCamera.position.set(length, length * 0.25, length);
		this.screenshotCamera.lookAt(center);
	}

	centerCamera() {
		const box = new Box3().setFromObject(this.renderer.project);
		const size = new Vector3();
		box.getSize(size);
		const length = size.length();

		this.controls.target.set(0, 0, 0);

		gsap.to(this.camera.position, {
			x: length,
			y: length * 0.25,
			z: length,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.controls.update();
			},
			onComplete: () => {},
		});
	}

	handleCameraZoom(zoom: number) {
		const target = this.controls.target.clone();
		const currentPosition = this.camera.position.clone();
		const direction = new Vector3()
			.subVectors(currentPosition, target)
			.normalize();

		const newPosition = target
			.clone()
			.add(direction.clone().multiplyScalar(zoom));

		gsap.to(this.camera.position, {
			x: newPosition.x,
			y: newPosition.y,
			z: newPosition.z,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.camera.lookAt(target);
			},
			onComplete: () => {
				this.controls.update();
			},
		});
	}

	handleResize(aspect: number) {
		this.camera.aspect = aspect;
		this.camera.updateProjectionMatrix();
	}

	dispose() {
		this.controls.dispose();
	}
}
